import { Component, computed, DestroyRef, inject, input, signal, Signal } from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { A11yModule } from '@angular/cdk/a11y';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import { Project } from '../models';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { catchError, EMPTY, finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-new-project-dialog',
  imports: [MatFormFieldModule, MatInputModule, A11yModule, MatButtonModule, RouterLink],
  templateUrl: './new-project-dialog.html',
  styleUrl: './new-project-dialog.scss',
})
export class NewProjectDialog {
  readonly existingProjects = input.required<Project[]>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly projectDomain = inject(ProjectDomainService);
  private readonly router = inject(Router);

  constructor() {
    const current = this.projectDomain.currentProject();
    if (current?.id) {
      this.projectName.set(current.name);
    }
  }

  projectName = signal('');
  isLoading = signal(false);
  apiError = signal<string | null>(null);

  readonly isUpdateOperation = computed(() => !!this.projectDomain.currentProject());

  errors = computed(() => {
    const name = this.projectName();
    const validationErrors: string[] = [];

    if (name.trim().length === 0) validationErrors.push('Project name is required');
    if (name.length > 100) validationErrors.push('Project name must be less than 100 characters');

    const nameExists = this.existingProjects().some((project) => project.name === name);
    if (nameExists) validationErrors.push('Project name already exists');

    const apiErr = this.apiError();
    if (apiErr) validationErrors.push(apiErr);

    return validationErrors;
  });

  onNameInput(e: Event): void {
    const el = e.target as HTMLInputElement | null;
    if (!el) return;
    this.projectName.set(el.value);
    this.apiError.set(null);
  }

  onReadyClick(): void {
    if (this.errors().length > 0) return;

    this.isLoading.set(true);
    this.apiError.set(null);

    const existingProject = this.projectDomain.currentProject();
    const isUpdateOperation = !!existingProject;

    const operation$ = isUpdateOperation
      ? this.projectDomain.updateProject({ ...existingProject, name: this.projectName() })
      : this.projectDomain.createProject(this.projectName());

    operation$
      .pipe(
        catchError((error) => {
          this.apiError.set(error?.message || 'Failed to save project');
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          console.log(`Project ${isUpdateOperation ? 'updated' : 'created'} successfully:`, result);
          this.navigateAfterSuccess(result, isUpdateOperation);
        },
      });
  }

  private navigateAfterSuccess(project: Project, wasUpdate: boolean): void {
    if (wasUpdate) {
      // Navigate to project detail after rename
      this.router.navigate(['/projects', project.id]);
    } else {
      // Navigate to project reports after create
      this.router.navigate(['/projects', project.id, 'reports']);
    }
  }
}
